/**
 * ConnectionStatusOverlay - Network connection state indicator displayed in top-right corner.
 * Shows status, client ID, and visual indicator (green/orange/red).
 */

export class ConnectionStatusOverlay {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement;
  private statusText: string = 'Disconnected';
  private clientId: string = '';

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /**
   * Show the connection status overlay
   */
  public show(): void {
    if (this.overlay) return;

    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      padding: 8px 12px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      z-index: 1000;
      pointer-events: none;
      border: 1px solid rgba(255, 255, 255, 0.2);
    `;

    this.container.appendChild(this.overlay);
    this.updateDisplay();
  }

  /**
   * Hide the connection status overlay
   */
  public hide(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  /**
   * Toggle the overlay visibility
   */
  public toggle(): void {
    if (this.overlay) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Update connection status
   */
  public setStatus(status: 'connecting' | 'connected' | 'disconnected', clientId?: string): void {
    this.statusText = status.charAt(0).toUpperCase() + status.slice(1);
    this.clientId = clientId ?? '';
    this.updateDisplay();
  }

  private updateDisplay(): void {
    if (!this.overlay) return;

    const statusColor = this.getStatusColor();
    const statusIcon = this.getStatusIcon();

    this.overlay.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="width: 8px; height: 8px; border-radius: 50%; background: ${statusColor};"></div>
        <div>
          <div style="font-weight: bold;">${statusIcon} ${this.statusText}</div>
          ${this.clientId ? `<div style="font-size: 10px; color: #aaa; margin-top: 2px;">${this.clientId}</div>` : ''}
        </div>
      </div>
    `;
  }

  private getStatusColor(): string {
    switch (this.statusText.toLowerCase()) {
      case 'connected':
        return '#4caf50'; // green
      case 'connecting':
        return '#ff9800'; // orange
      case 'disconnected':
      default:
        return '#f44336'; // red
    }
  }

  private getStatusIcon(): string {
    switch (this.statusText.toLowerCase()) {
      case 'connected':
        return '✓';
      case 'connecting':
        return '⟳';
      case 'disconnected':
      default:
        return '✗';
    }
  }

  /**
   * Check if overlay is currently visible
   */
  public isVisible(): boolean {
    return this.overlay !== null;
  }
}

